/**
 * Clears all select-field label data from a Formly form state and related data objects.
 *
 * What this utility does:
 * - Identifies all fields of type 'select' from the provided Formly configuration.
 * - Removes the corresponding select values from the form model.
 * - Removes default values from select fields to prevent automatic re-population.
 * - Updates matching data items and strips stored select values while preserving
 *   the remaining metadata of each select field.
 * - Optionally clears existing annotation data and resets annotation statistics.
 * - Removes select field values from the persisted label state.
 *
 * Typical use case:
 * When a labeling/classification is reset and all previously selected labels,
 * annotations, and related form state should be cleared before starting a new
 * labeling process.
 *
 * Returns:
 * - The list of affected select field keys.
 * - The cleaned form model.
 * - The updated item (if an item was found by ID).
 */
import { FormlyFieldConfig } from '@ngx-formly/core';

export interface ClearLabelsParams {
  fields: FormlyFieldConfig[];
  model: any;
  dataArray?: any[];
  id?: string;
  data?: any;
  labelStateKey: string;
  legacyAnnotationsKey: string;
  clearAnnotations: boolean;
}

export interface ClearLabelsResult {
  selectKeys: string[];
  model: any;
  currentItem?: any;
}

export function clearLabelsState(params: ClearLabelsParams): ClearLabelsResult {
  const selectKeys = collectSelectKeys(params.fields);
  const nextModel = removeSelectKeysFromModel(params.model, selectKeys);

  let currentItem: any;
  if (params.dataArray && params.id) {
    for (const item of params.dataArray) {
      if (item?.id?.key === params.id) {
        clearItemLabelData(item, selectKeys, params);
        currentItem = item;
      }
    }
  }

  if (params.data) {
    clearItemLabelData(params.data, selectKeys, params);
  }

  return {
    selectKeys,
    model: nextModel,
    currentItem,
  };
}

function collectSelectKeys(fields: FormlyFieldConfig[]): string[] {
  const selectKeys: string[] = [];
  for (const field of (fields[0]?.fieldGroup ?? [])) {
    if ((field as any).type === 'select') {
      selectKeys.push(field.key as string);
      delete field.defaultValue;
    }
  }
  return selectKeys;
}

function removeSelectKeysFromModel(model: any, selectKeys: string[]): any {
  const nextModel: any = { ...(model ?? {}) };
  for (const key of selectKeys) {
    delete nextModel[key];
  }
  return nextModel;
}

function clearItemLabelData(item: any, selectKeys: string[], params: ClearLabelsParams): void {
  delete item['_savedByUser'];

  for (const key of selectKeys) {
    if (item?.[key] && typeof item[key] === 'object' && item[key].type === 'select') {
      const { value, ...rest } = item[key];
      void value;
      item[key] = rest;
    }
  }

  if (params.clearAnnotations) {
    item[params.legacyAnnotationsKey] = [];
  }

  const labelState = item?.[params.labelStateKey];
  if (!labelState) {
    return;
  }

  if (labelState.form?.values) {
    for (const key of selectKeys) {
      delete labelState.form.values[key];
    }
  }

  if (params.clearAnnotations && labelState.annotations) {
    labelState.annotations.items = [];
    labelState.annotations.stats = {
      countByType: {},
      countByClass: {},
    };
  }
}
